import { FunctionComponent, useEffect, useState, FormEvent, useCallback } from "react";
import { useRouter } from "next/router";
import { Heading } from "../../../../components/lib/Text/Heading";
import Spinner from "../../../../components/lib/Spinner/Spinner";
import Button from "../../../../components/lib/Button/Button";
import { FormShortId } from "../../../../lib/types/ids";
import * as t from "io-ts";
import { FormHandle } from "../../../../lib/types/form/handle";
import TextField from "../../../../components/lib/TextField/TextField";
import { requestCancelSite, RequestCancelSiteResponse } from "../../../../lib/api/cancel/[handle]/[shortId]/request-site";
import { UnexpectedValueError } from "../../../../lib/unexpected-value-error";
import { PlainMemberEmail } from "../../../../lib/types/user/memberEmail";
import { UnreachableVariantError } from "../../../../lib/unreachable-variant-error";

// Hier kann die Kündigungsseite durch den Benutzer angefordert werden.
// Der Benutzer bekommt diesen Link per Mail.

const RouterQueryArgs = t.partial({
  handle: FormHandle,
  id: FormShortId,
});

type SubmitResult = RequestCancelSiteResponse;

const RequestCancelContractPage: FunctionComponent = () => {
  const router = useRouter();
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [submitResult, setSubmitResult] = useState<SubmitResult>();

  let content;
  const getQuery = useCallback(() => {
    const queryDecoded = RouterQueryArgs.decode(router.query);
    if (queryDecoded._tag !== "Right") return {};
    return queryDecoded.right;
  }, [router]);
  const query = getQuery();
  useEffect(() => {
    const query = getQuery();
    const id = query.id;
    const handle = query.handle;
    if (id && handle) {
      setIsSubmitted(false);
      setSubmitResult(undefined);
    }
  }, [getQuery, query]);

  const onRequestCancelSite = async (
    e: FormEvent<HTMLFormElement>
  ) => {
    if (e) {
      e.preventDefault();
    }
    if (isSubmitted || submitResult) return;

    const emailElement = e.currentTarget.elements.namedItem("Email");
    if (!(emailElement instanceof HTMLInputElement)) return;
    const email = emailElement.value;
    if (email?.trim()) {
      const query = getQuery();
      const id = query.id;
      const handle = query.handle;
      if (id && handle) {
        setIsSubmitted(true);
        setSubmitResult(undefined);
        console.log(`Submitted: ${email}`);

        try {
          const res = await requestCancelSite({
            handle,
            shortId: id,
            email: UnexpectedValueError.throwIfNot("plain member email",
              PlainMemberEmail, email),
          });
          setSubmitResult(res);
        } catch (err) {
          console.error(err);
        }
      }
    }
  };
  if (!isSubmitted) {
    content = (
      <>
        <Heading level={3} className="mb-8">
          Kündigungsseite anfordern
        </Heading>
        <p className="mb-8">
          Um dich und deine Daten zu schützen, musst du bevor du deinen Vertrag
          kündigen kannst, deine Emailadresse eingeben, welche mit dem Vertrag
          angegeben wurde. An diese Adresse wird der Link zu deiner persönlichen
          Kündigungsseite geschickt.
        </p>
        <form className="flex flex-col w-full" onSubmit={e => { onRequestCancelSite(e); }}>
          <div className="mb-2">
            <TextField
              label="Email"
              placeholder="Email"
              name="Email"
              type="email"
              showLabel={false}
              required={true}
            />
          </div>
          <Button variant="primary" type="submit">
            Absenden
          </Button>
        </form>
      </>
    );
  } else {
    switch (submitResult) {
      case undefined:
        content = (
          <>
            <Heading level={3} className="mb-8">
              Die Anfrage wird verarbeitet
            </Heading>
            <Spinner />
          </>
        );
        break;

      default:
        switch (submitResult.type) {
          case "not-found":
            content = <p>Wir konnten leider nichts finden.</p>;
            break;

          case "unexpected-error":
            content = <p>Es ist ein unerwarteter Fehler aufgetreten.</p>;
            break;

          case "mail-failed":
            content = <p>Die Email konnte nicht versendet werden.</p>;
            break;

          case "success":
            content = (
              <p>
                An die angegebene Emailadresse wurde eine Email mit dem Code <b>{submitResult.code}</b> geschickt. Mit dem in der Email enthaltenen Link kann die persönliche Kündigungsseite aufgerufen werden.
              </p>
            );
            break;

          default:
            throw new UnreachableVariantError(submitResult);
        }

    }
  }

  return (
    <div className="theme-admin h-full w-full flex min-h-screen text-center justify-center pt-32">
      <div className="max-w-lg w-full h-full flex flex-col items-center justify-center py-10 px-8 border rounded-3xl border-gray-300">
        {content}
      </div>
    </div>
  );
};

export default RequestCancelContractPage;
